/**
 * Enrollment API client (Sprint 4 B5).
 *
 * Mirror backend endpoints:
 *   - POST   /api/enrollment/courses/<slug>/enroll/  — enroll (idempotent)
 *   - DELETE /api/enrollment/courses/<slug>/enroll/  — drop
 *   - GET    /api/enrollment/me/?status=<value>      — list my enrollments
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";

// ─── Types ────────────────────────────────────────────────────────────────

export type EnrollmentStatus = "enrolled" | "completed" | "dropped" | "expired";

export interface EnrolledCourse {
  id: number;
  code: string;
  slug: string;
  name: string;
  credits: number;
  cover_image: string | null;
  term_names: string[];
  lesson_count: number;
}

export interface Enrollment {
  id: number;
  status: EnrollmentStatus;
  enrolled_at: string;
  completed_at: string | null;
  dropped_at: string | null;
  course: EnrolledCourse;
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ─── Hooks ────────────────────────────────────────────────────────────────

/**
 * GET /api/enrollment/me/?status=<value>
 *
 * `status` filter optional — FE tab "Đang học/Đã xong/Đã bỏ" pass tương ứng.
 */
export function useMyEnrollments(statusFilter?: EnrollmentStatus) {
  return useQuery({
    queryKey: ["enrollment", "me", statusFilter ?? "all"],
    queryFn: async () => {
      const params = statusFilter ? { status: statusFilter } : {};
      const resp = await apiClient.get<Paginated<Enrollment>>("/enrollment/me/", { params });
      // DRF paginated → trả results trực tiếp cho FE consume dễ
      return resp.data.results;
    },
    staleTime: 30_000, // 30s — cập nhật nhanh sau khi enroll/drop
  });
}

/**
 * POST /api/enrollment/courses/<slug>/enroll/
 *
 * Idempotent — 201 nếu mới, 200 nếu đã enrolled hoặc re-enroll sau drop.
 * Invalidate `["enrollment", ...]` cache → tự refetch `useMyEnrollments`
 * + `useIsEnrolledIn` (xem dưới).
 */
export function useEnroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (courseSlug: string) => {
      const resp = await apiClient.post<Enrollment>(`/enrollment/courses/${courseSlug}/enroll/`);
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enrollment"] });
    },
  });
}

/**
 * DELETE /api/enrollment/courses/<slug>/enroll/  → 204 (set DROPPED).
 */
export function useDropEnrollment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (courseSlug: string) => {
      await apiClient.delete(`/enrollment/courses/${courseSlug}/enroll/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enrollment"] });
    },
  });
}

/**
 * Helper: tìm enrollment status của user cho 1 course cụ thể.
 *
 * Dùng `useMyEnrollments` (cache shared) + filter client-side — không tạo
 * API call mới mỗi course. Trả về enrollment object hoặc null.
 */
export function useEnrollmentFor(courseSlug: string | undefined) {
  const { data: enrollments, isLoading } = useMyEnrollments();
  const match = enrollments?.find((e) => e.course.slug === courseSlug);
  return { enrollment: match ?? null, isLoading };
}

// ─── UserMajor self-service (Sprint 26 — User↔Program/Major) ──────────────

export type MajorEnrollmentStatus = "enrolled" | "completed" | "dropped";

export interface MyMajorProgram {
  id: number;
  code: string;
  slug: string;
  name: string;
}

export interface MyMajor {
  id: number;
  code: string;
  slug: string;
  name: string;
  program: MyMajorProgram;
}

export interface MyMajorEnrollment {
  id: number;
  status: MajorEnrollmentStatus;
  enrolled_at: string;
  completed_at: string | null;
  dropped_at: string | null;
  major: MyMajor;
}

/**
 * GET /api/enrollment/me/majors/
 *
 * Ngành (Major) của user hiện tại — Program suy ra qua `major.program`.
 * KHÔNG có bảng UserProgram riêng (chốt thiết kế 2026-06-30).
 */
export function useMyMajors() {
  return useQuery({
    queryKey: ["enrollment", "me", "majors"],
    queryFn: async () => {
      const resp = await apiClient.get<Paginated<MyMajorEnrollment>>("/enrollment/me/majors/");
      return resp.data.results;
    },
    staleTime: 30_000,
  });
}

// ─── Gợi ý môn học theo kỳ hiện tại (Sprint 31) ────────────────────────────

export interface MissingPrerequisite {
  id: number;
  code: string;
  name: string;
}

export interface SuggestedCourse {
  id: number;
  code: string;
  slug: string;
  name: string;
  credits: number;
  is_required: boolean;
  blocked: boolean;
  missing_prerequisites: MissingPrerequisite[];
  /** Sprint 34 — giỏ hàng hiện giá. Null nếu PER_CREDIT chưa cấu hình học phí. */
  effective_price_cents: number | null;
}

export interface SuggestedCurrentTerm {
  id: number;
  name: string;
  order: number;
}

export type SuggestedCoursesReason =
  | "no_active_major"
  | "no_terms_defined"
  | "completed_all_terms"
  | null;

export interface SuggestedCoursesResponse {
  major_id: number | null;
  major_name: string | null;
  current_term: SuggestedCurrentTerm | null;
  reason: SuggestedCoursesReason;
  courses: SuggestedCourse[];
}

/**
 * GET /api/enrollment/me/suggested-courses/?major_id=<id>
 *
 * Gợi ý môn học nên đăng ký cho kỳ hiện tại của Major (chốt 2026-07-09) —
 * xem `backend/apps/enrollment/services.py::current_curriculum_term`.
 *
 * `majorId` bắt buộc để hook fire — caller phải tự resolve đúng major trước
 * (vd `CourseList` chỉ gọi khi user có đúng 1 UserMajor đang enrolled,
 * `MajorPublicDetail` gọi sau khi major detail đã load xong `major.id`).
 * Tránh gọi API với major_id rỗng lúc trang còn loading — backend tự chọn
 * major "mặc định" khác với major đang xem, có thể hiện sai kỳ hiện tại
 * thoáng qua hoặc 400 nếu user có nhiều ngành.
 */
export function useSuggestedCourses(majorId: number | undefined) {
  return useQuery({
    queryKey: ["enrollment", "me", "suggested-courses", majorId ?? null],
    enabled: majorId !== undefined,
    queryFn: async () => {
      const params = majorId ? { major_id: majorId } : undefined;
      const resp = await apiClient.get<SuggestedCoursesResponse>(
        "/enrollment/me/suggested-courses/",
        { params },
      );
      return resp.data;
    },
    staleTime: 30_000,
  });
}

// ─── Trạng thái đăng ký toàn bộ khung chương trình — "học vượt" (Sprint 35) ─

export interface CourseEligibility {
  slug: string;
  blocked: boolean;
  missing_prerequisites: MissingPrerequisite[];
  effective_price_cents: number | null;
}

export interface MajorCourseEligibilityResponse {
  major_id: number | null;
  major_name: string | null;
  courses: CourseEligibility[];
}

/**
 * GET /api/enrollment/me/major-course-eligibility/?major_id=<id>
 *
 * Trạng thái đăng ký (blocked/missing_prerequisites/effective_price_cents)
 * của TOÀN BỘ môn thuộc `major` — MỌI kỳ, không chỉ kỳ hiện tại (khác
 * `useSuggestedCourses`). Chốt 2026-07-13 — cho phép "học vượt": trang Kế
 * hoạch học tập thêm giỏ hàng bất kỳ môn nào, không giới hạn kỳ hiện tại.
 */
export function useMajorCourseEligibility(majorId: number | undefined) {
  return useQuery({
    queryKey: ["enrollment", "me", "major-course-eligibility", majorId ?? null],
    enabled: majorId !== undefined,
    queryFn: async () => {
      const params = majorId ? { major_id: majorId } : undefined;
      const resp = await apiClient.get<MajorCourseEligibilityResponse>(
        "/enrollment/me/major-course-eligibility/",
        { params },
      );
      return resp.data;
    },
    staleTime: 30_000,
  });
}

// ─── Trạng thái đợt đăng ký hiện tại — cảnh báo chủ động (Sprint 35) ───────

export interface RegistrationWindowTermOffering {
  id: number;
  name: string;
  registration_closes_at: string | null;
}

export interface RegistrationWindow {
  is_open: boolean;
  term_offering: RegistrationWindowTermOffering | null;
}

/**
 * GET /api/enrollment/me/registration-window/
 *
 * Trạng thái đợt đăng ký môn học HIỆN TẠI của tenant — khác các API
 * enroll/checkout (chỉ gate LÚC submit, trả 400 nếu ngoài đợt). Endpoint này
 * cho FE hỏi TRƯỚC để cảnh báo + disable nút "Thêm vào giỏ hàng", tránh sinh
 * viên chọn xong môn rồi mới phát hiện bị chặn lúc checkout (chốt
 * 2026-07-13). Không có tham số — cùng 1 query key cho mọi component gọi,
 * React Query tự dedupe (không cần truyền prop xuống nhiều tầng).
 */
export function useRegistrationWindow() {
  return useQuery({
    queryKey: ["enrollment", "me", "registration-window"],
    queryFn: async () => {
      const resp = await apiClient.get<RegistrationWindow>("/enrollment/me/registration-window/");
      return resp.data;
    },
    staleTime: 30_000,
  });
}

// ─── Tiến độ tín chỉ theo ngành (Sprint 35) ─────────────────────────────────

export interface MajorCredits {
  required_credits_target: number;
  elective_credits_target: number;
  required_credits_earned: number;
  elective_credits_earned: number;
  required_credits_remaining: number;
  elective_credits_remaining: number;
  total_credits_earned: number;
}

export interface MajorCreditsResponse {
  major_id: number | null;
  major_name: string | null;
  credits: MajorCredits | null;
}

/**
 * GET /api/enrollment/me/major-credits/?major_id=<id>
 *
 * Tổng tín chỉ đã tích lũy (môn COMPLETED) tách bắt buộc/tự chọn, đối chiếu
 * mục tiêu tín chỉ của ngành (`Major.required_credits`/`elective_credits`,
 * admin nhập ở `/admin/curriculum`) — chốt 2026-07-13.
 */
export function useMajorCredits(majorId: number | undefined) {
  return useQuery({
    queryKey: ["enrollment", "me", "major-credits", majorId ?? null],
    enabled: majorId !== undefined,
    queryFn: async () => {
      const params = majorId ? { major_id: majorId } : undefined;
      const resp = await apiClient.get<MajorCreditsResponse>("/enrollment/me/major-credits/", {
        params,
      });
      return resp.data;
    },
    staleTime: 30_000,
  });
}

// ─── Giỏ hàng chọn môn — bulk enroll (Sprint 33) ───────────────────────────
//
// Giỏ hàng CHỈ tồn tại phía FE (React state ở `SuggestedCourses`, không lưu
// DB) — chọn nhiều slug rồi gửi 1 request duy nhất. Đợt đăng ký resolve MỘT
// LẦN cho cả batch ở backend — ngoài đợt đăng ký thì toàn bộ request 400
// (dùng `extractEnrollErrorMessage` như enroll đơn), không phải lỗi từng
// dòng. Xem `backend/apps/enrollment/views.py::BulkEnrollView`.

export type BulkEnrollStatus =
  | "enrolled"
  | "already_enrolled"
  | "blocked_prerequisites"
  | "requires_payment"
  | "not_found";

export interface BulkEnrollResult {
  course_slug: string;
  status: BulkEnrollStatus;
  detail: string;
  missing_prerequisites?: MissingPrerequisite[];
}

export interface BulkEnrollResponse {
  results: BulkEnrollResult[];
}

/**
 * POST /api/enrollment/courses/bulk-enroll/  body `{course_slugs: string[]}`
 *
 * Luôn 200 kèm `results[]` khi qua được gate đợt đăng ký (mỗi slug 1 dòng
 * kết quả độc lập) — 400 CHỈ khi cả batch bị chặn ở gate chung (ngoài đợt
 * đăng ký, payload rỗng/sai kiểu...).
 */
export function useBulkEnroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (courseSlugs: string[]) => {
      const resp = await apiClient.post<BulkEnrollResponse>("/enrollment/courses/bulk-enroll/", {
        course_slugs: courseSlugs,
      });
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enrollment"] });
    },
  });
}

interface EnrollErrorBody {
  detail?: string;
  missing_prerequisites?: MissingPrerequisite[];
}

/**
 * Rút thông báo lỗi thân thiện từ response 400 của
 * `POST /enrollment/.../enroll/` hoặc `POST /payment/.../checkout/`.
 *
 * BE trả 3 dạng 400 (xem `apps/enrollment/services.py`):
 *   - thiếu prerequisites/pre_courses → kèm `missing_prerequisites` (liệt kê tên môn)
 *   - hiện không trong đợt đăng ký môn học nào (registration window)
 *   - nhiều kỳ đang mở đăng ký cùng lúc (data admin set trùng)
 * Cả 3 đều có `detail` — chỉ `missing_prerequisites` cần format thêm.
 */
export function extractEnrollErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: EnrollErrorBody } })?.response?.data;
  if (data?.missing_prerequisites?.length) {
    const names = data.missing_prerequisites.map((c) => `${c.code} - ${c.name}`).join(", ");
    // Danh sách gộp chung prerequisites (phải hoàn thành) + pre_courses (chỉ
    // cần đã từng học) — không phân biệt loại nào trong response, nên dùng
    // câu trung tính, không khẳng định "cần hoàn thành" (sai với pre_courses).
    return `Bạn chưa đủ điều kiện đăng ký, còn thiếu: ${names}.`;
  }
  return data?.detail ?? fallback;
}
